import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { deskApi } from '@/api/desk.api';
import { showApiError } from '@/utils/apiError';
import type { DeskInboxConversation, DeskInboxMessage } from '@/types/desk';
import { notify } from '@/core/toast/notify';

const LIVE_SOURCES = new Set(['LIVECHAT', 'WHATSAPP', 'SOCIAL']);

export function DeskLiveChatPage() {
  const [live, setLive] = useState<DeskInboxConversation[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [messages, setMessages] = useState<DeskInboxMessage[]>([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    deskApi
      .listInboxConversations()
      .then((rows) => setLive(rows.filter((c) => LIVE_SOURCES.has(c.source))))
      .catch((err) => showApiError(err, 'Failed to load live chats'));
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 15_000);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (selectedId == null) {
      setMessages([]);
      return;
    }
    deskApi.listInboxMessages(selectedId).then(setMessages).catch((err) => showApiError(err, 'Failed to load thread'));
  }, [selectedId]);

  const sendReply = async () => {
    if (selectedId == null || !reply.trim()) return;
    setSending(true);
    try {
      const sent = await deskApi.replyInboxConversation(selectedId, reply.trim());
      setReply('');
      setMessages((prev) => [...prev, sent]);
      notify.success('Reply sent');
      load();
    } catch (err) {
      showApiError(err, 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Live Chat"
        description="Realtime customer conversations"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Desk', to: '/desk' }, { label: 'Live Chat' }]}
      />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-1 max-h-[70vh] overflow-y-auto">
          {live.length === 0 ? (
            <p className="text-sm text-slate-600">
              No active live chats. Social and WhatsApp threads routed to Desk appear here automatically.
            </p>
          ) : (
            <ul className="space-y-2">
              {live.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className={`w-full text-left rounded-lg border p-3 text-sm ${
                      selectedId === c.id ? 'border-primary-500 bg-primary-50' : 'border-slate-200'
                    }`}
                    onClick={() => setSelectedId(c.id)}
                  >
                    <div className="font-medium">{c.ticketNumber ?? `Chat #${c.id}`}</div>
                    <div className="text-xs text-slate-500">{c.source}</div>
                    <div className="mt-1 truncate text-slate-600">{c.preview ?? '—'}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card p-4 lg:col-span-2 flex flex-col max-h-[70vh]">
          {selectedId == null ? (
            <p className="text-sm text-slate-500">Select a conversation</p>
          ) : (
            <>
              <ul className="flex-1 space-y-3 overflow-y-auto mb-4">
                {messages.map((m) => (
                  <li
                    key={m.id}
                    className={`rounded-lg p-3 text-sm ${
                      m.direction === 'INBOUND' ? 'bg-slate-100' : 'bg-primary-50'
                    }`}
                  >
                    <div className="text-xs text-slate-500 mb-1">
                      {m.senderType} · {m.direction} · {m.sentAt ? new Date(m.sentAt).toLocaleString() : ''}
                    </div>
                    <p className="whitespace-pre-wrap">{m.messageContent}</p>
                  </li>
                ))}
              </ul>
              <div className="border-t pt-3 space-y-2">
                <Input label="Agent reply" value={reply} onChange={(e) => setReply(e.target.value)} />
                <Button onClick={sendReply} loading={sending}>Send reply</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
